import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await api.get('/wishlist');
      setWishlistItems(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await api.delete(`/wishlist/${productId}`);
      setWishlistItems(wishlistItems.filter(item => item.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleAddToCart = async (product) => {
    if (product.stock_status !== 'in_stock') {
      toast.error('This item is not available');
      return;
    }
    await addToCart(product.id);
    await handleRemove(product.id);
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-serif text-4xl mb-2">My Wishlist</h1>
          <p className="text-gray-600">{wishlistItems.length} items saved</p>
        </div>

        {wishlistItems.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="font-serif text-2xl mb-2">Your wishlist is empty</h2>
            <p className="text-gray-600 mb-6">Save your favorite gemstones here</p>
            <Button onClick={() => navigate('/shop')} className="bg-black text-white hover:bg-gray-900">
              Browse Gemstones
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {wishlistItems.map((product) => (
              <div key={product.id} className="border border-gray-200 group" data-testid={`wishlist-item-${product.id}`}>
                <div 
                  onClick={() => navigate(`/products/${product.id}`)}
                  className="cursor-pointer"
                >
                  <div className="aspect-square bg-gray-50 product-image-wrapper">
                    <img
                      src={product.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=400&q=80'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-gray-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="text-xs text-gray-600">
                      {product.carat_weight} ct • {product.origin}
                    </div>
                    <p className="text-sm font-semibold">
                      {product.price_on_request ? 'POA' : formatPrice(product.price, product.currency)}
                    </p>
                  </div>
                </div>
                
                <div className="px-4 pb-4 flex gap-2">
                  {product.stock_status === 'in_stock' && (
                    <Button
                      onClick={() => handleAddToCart(product)}
                      size="sm"
                      className="flex-1 bg-black text-white hover:bg-gray-900"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Add to Cart
                    </Button>
                  )}
                  <Button
                    onClick={() => handleRemove(product.id)}
                    size="sm"
                    variant="outline"
                    className="border-gray-300 hover:border-red-500 hover:text-red-500"
                    data-testid={`remove-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
